'use client';

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Row,
  Col,
  Space,
  Button,
  Input,
  Select,
  Modal,
  Form,
  Popconfirm,
  Descriptions,
} from 'antd';
import { toast } from 'react-toastify';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  AppstoreOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import type { UomRecord } from '@/lib/mockUomStore';
import { useAuth } from '@/components/AuthContext';
import { MASTER_ITEMS } from '@/lib/masterItems';

const { Title, Text, Paragraph } = Typography;

export default function UomPage() {
  const { user } = useAuth();
  const isManager = user.roles.includes('ADMIN') || user.roles.includes('PURCHASER') || user.roles.includes('WAREHOUSE');

  const [loading, setLoading] = useState(true);
  const [uoms, setUoms] = useState<UomRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');

  // Create / Edit Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUom, setEditingUom] = useState<UomRecord | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // Detail Dialog State (Requested: "untuk detail nya cukup dibuatkan dialog")
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedUomDetail, setSelectedUomDetail] = useState<(UomRecord & { linkedItems: typeof MASTER_ITEMS }) | null>(null);

  const fetchUoms = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/uom');
      const json = await res.json();
      if (json.success) {
        setUoms(json.data);
      }
    } catch {
      toast.error('Gagal memuat data satuan UoM');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUoms();
  }, [fetchUoms]);

  // Open Create Modal
  const handleOpenCreate = () => {
    setEditingUom(null);
    form.resetFields();
    form.setFieldsValue({
      category: 'Count',
    });
    setModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (record: UomRecord) => {
    setEditingUom(record);
    form.setFieldsValue({
      code: record.code,
      name: record.name,
      symbol: record.symbol,
      category: record.category,
      description: record.description,
    });
    setModalOpen(true);
  };

  // Open Detail Dialog
  const handleOpenDetail = async (record: UomRecord) => {
    try {
      const res = await fetch(`/api/uom/${record.id}`);
      const json = await res.json();
      if (json.success) {
        setSelectedUomDetail(json.data);
        setDetailModalOpen(true);
      } else {
        toast.error(json.error || 'Gagal memuat detail satuan');
      }
    } catch {
      toast.error('Gagal menghubungi server untuk detail satuan');
    }
  };

  // Submit Create / Edit
  const handleSubmitForm = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const payload = {
        code: values.code,
        name: values.name,
        symbol: values.symbol,
        category: values.category,
        description: values.description,
      };

      let res: Response;
      if (editingUom) {
        res = await fetch(`/api/uom/${editingUom.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch('/api/uom', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Gagal menyimpan satuan');
      }

      toast.success(json.message || 'Satuan berhasil disimpan');
      setModalOpen(false);
      fetchUoms();
    } catch (err: unknown) {
      if (err instanceof Error) {
        toast.error(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Delete UoM
  const handleDeleteUom = async (id: string) => {
    try {
      const res = await fetch(`/api/uom/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Gagal menghapus satuan');
      }
      toast.success(json.message || 'Satuan berhasil dihapus');
      fetchUoms();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Gagal menghapus satuan');
    }
  };

  // Filter UoMs
  const filteredUoms = uoms.filter((u) => {
    const q = searchQuery.toLowerCase().trim();
    const matchSearch =
      u.code.toLowerCase().includes(q) ||
      u.name.toLowerCase().includes(q) ||
      u.symbol.toLowerCase().includes(q) ||
      u.description.toLowerCase().includes(q);

    if (!matchSearch) return false;
    if (categoryFilter !== 'ALL' && u.category !== categoryFilter) return false;

    return true;
  });

  const columns = [
    {
      title: 'Kode Satuan',
      dataIndex: 'code',
      key: 'code',
      width: 130,
      render: (c: string, r: UomRecord) => (
        <Tag
          color="cyan"
          onClick={() => handleOpenDetail(r)}
          style={{
            cursor: 'pointer',
            fontWeight: 800,
            fontSize: 12,
            padding: '2px 8px',
            borderRadius: 6,
          }}
        >
          {c}
        </Tag>
      ),
    },
    {
      title: 'Nama Satuan Ukur',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, r: UomRecord) => (
        <div>
          <span
            style={{ fontWeight: 700, color: '#0F172A', fontSize: 13, cursor: 'pointer' }}
            onClick={() => handleOpenDetail(r)}
          >
            {name}
          </span>
          {r.description && (
            <div
              style={{
                color: '#64748B',
                fontSize: 12,
                marginTop: 2,
                maxWidth: 400,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={r.description}
            >
              {r.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Simbol',
      dataIndex: 'symbol',
      key: 'symbol',
      width: 110,
      render: (sym: string) => (
        <Tag style={{ borderRadius: 4, fontWeight: 600 }}>{sym}</Tag>
      ),
    },
    {
      title: 'Kategori Dimensi',
      dataIndex: 'category',
      key: 'category',
      width: 140,
      render: (cat: string) => {
        let color = 'default';
        if (cat === 'Length') color = 'purple';
        if (cat === 'Weight') color = 'orange';
        if (cat === 'Count') color = 'blue';
        if (cat === 'Packaging') color = 'green';
        return (
          <Tag color={color} style={{ borderRadius: 6, fontSize: 11 }}>
            {cat}
          </Tag>
        );
      },
    },
    {
      title: 'Aksi',
      key: 'action',
      width: 170,
      align: 'right' as const,
      render: (_: unknown, r: UomRecord) => (
        <Space size="small">
          <Button
            type="default"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleOpenDetail(r)}
            style={{ borderRadius: 6, color: '#6366F1', borderColor: '#C7D2FE', fontSize: 12 }}
          >
            Detail
          </Button>
          {isManager && (
            <>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleOpenEdit(r)}
                style={{ borderRadius: 6 }}
              />
              <Popconfirm
                title="Hapus Satuan UoM"
                description={`Apakah Anda yakin ingin menghapus '${r.code} - ${r.name}'? Satuan yang sedang digunakan item katalog tidak dapat dihapus.`}
                onConfirm={() => handleDeleteUom(r.id)}
                okText="Ya, Hapus"
                cancelText="Batal"
                okButtonProps={{ danger: true }}
              >
                <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 6 }} />
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', paddingBottom: 40 }}>
      {/* Header Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Title level={3} style={{ margin: 0, color: '#0F172A', fontWeight: 800 }}>
              Units of Measure (UoM)
            </Title>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#0369A1',
                background: '#E0F2FE',
                border: '1px solid #BAE6FD',
                padding: '3px 10px',
                borderRadius: 20,
              }}
            >
              Standarisasi Satuan
            </span>
          </div>
          <Text type="secondary" style={{ fontSize: 13, marginTop: 4, display: 'block' }}>
            Standarisasi satuan ukur untuk permintaan pembelian (PR), pesanan pembelian (PO), penerimaan gudang (GR), dan inventaris pabrik.
          </Text>
        </div>

        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchUoms} style={{ borderRadius: 10 }}>
            Segarkan
          </Button>
          {isManager && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenCreate}
              style={{
                borderRadius: 10,
                background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
                fontWeight: 600,
              }}
            >
              Tambah Satuan Baru
            </Button>
          )}
        </Space>
      </div>

      {/* KPI Pastel Stat Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #EEF2FF 100%)',
              padding: '18px 20px',
              borderRadius: 14,
              border: '1px solid #E0E7FF',
              boxShadow: '0 4px 16px -2px rgba(99, 102, 241, 0.05)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#4F46E5', textTransform: 'uppercase' }}>
              Total Satuan Terdaftar
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#1E1B4B', marginTop: 4 }}>
              {uoms.length}{' '}
              <span style={{ fontSize: 13, fontWeight: 600, color: '#4F46E5' }}>Satuan Ukur</span>
            </div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>Standar pengukuran aktif</div>
          </div>
        </Col>

        <Col xs={24} sm={8}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F3FF 100%)',
              padding: '18px 20px',
              borderRadius: 14,
              border: '1px solid #DDD6FE',
              boxShadow: '0 4px 16px -2px rgba(124, 58, 237, 0.05)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6D28D9', textTransform: 'uppercase' }}>
              Satuan Tekstil & Panjang
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#4C1D95', marginTop: 4 }}>
              MTR & YARD
            </div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>Pengukuran kain roll dan pita</div>
          </div>
        </Col>

        <Col xs={24} sm={8}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F0FDF4 100%)',
              padding: '18px 20px',
              borderRadius: 14,
              border: '1px solid #BBF7D0',
              boxShadow: '0 4px 16px -2px rgba(16, 185, 129, 0.05)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#15803D', textTransform: 'uppercase' }}>
              Satuan Kemasan & Hitung
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#064E3B', marginTop: 4 }}>
              PCS, ROLL, BOX
            </div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>Satuan logistik & pengemasan</div>
          </div>
        </Col>
      </Row>

      {/* Main Table Card */}
      <Card
        style={{
          borderRadius: 16,
          border: '1px solid #F1F5F9',
          boxShadow: '0 4px 20px -2px rgba(99, 102, 241, 0.04)',
        }}
        styles={{ body: { padding: '18px 20px' } }}
      >
        {/* Search & Filter Bar */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={14}>
            <Input
              placeholder="Cari berdasarkan Kode Satuan (PCS, MTR...), Nama, atau Simbol..."
              prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ borderRadius: 10 }}
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              style={{ width: '100%', borderRadius: 10 }}
              options={[
                { value: 'ALL', label: 'Semua Kategori Dimensi' },
                { value: 'Count', label: 'Count (Satuan Hitung Unit)' },
                { value: 'Length', label: 'Length (Satuan Panjang)' },
                { value: 'Weight', label: 'Weight (Satuan Berat)' },
                { value: 'Packaging', label: 'Packaging (Satuan Kemasan)' },
              ]}
            />
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredUoms}
          loading={loading}
          pagination={false}
          size="middle"
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={editingUom ? 'Edit Satuan Ukur (UoM)' : 'Tambah Satuan Ukur Baru (UoM)'}
        open={modalOpen}
        onOk={handleSubmitForm}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitting}
        okText={editingUom ? 'Simpan Perubahan' : 'Tambahkan Satuan'}
        cancelText="Batal"
        width={560}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={10}>
              <Form.Item
                name="code"
                label="Kode Satuan (Code)"
                rules={[{ required: true, message: 'Kode satuan wajib diisi' }]}
                tooltip="Huruf kapital singkat (contoh: PCS, MTR, KG, ROLL, BOX)"
              >
                <Input placeholder="Contoh: MTR" style={{ borderRadius: 8, textTransform: 'uppercase' }} />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item
                name="name"
                label="Nama Lengkap Satuan"
                rules={[{ required: true, message: 'Nama satuan wajib diisi' }]}
              >
                <Input placeholder="Contoh: Meter" style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={10}>
              <Form.Item
                name="symbol"
                label="Simbol Singkatan"
                rules={[{ required: true, message: 'Simbol satuan wajib diisi' }]}
              >
                <Input placeholder="Contoh: m" style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item
                name="category"
                label="Kategori Dimensi"
                rules={[{ required: true, message: 'Kategori wajib dipilih' }]}
              >
                <Select
                  options={[
                    { value: 'Count', label: 'Count (Hitung Unit)' },
                    { value: 'Length', label: 'Length (Panjang Tekstil)' },
                    { value: 'Weight', label: 'Weight (Berat / Massa)' },
                    { value: 'Packaging', label: 'Packaging (Kemasan / Logistik)' },
                    { value: 'General', label: 'General (Umum)' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Deskripsi Penggunaan">
            <Input.TextArea
              rows={3}
              placeholder="Jelaskan standar penggunaan satuan ini dalam operasional..."
              style={{ borderRadius: 8 }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Dialog / Modal (Requested by Captain) */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <InfoCircleOutlined style={{ color: '#6366F1' }} />
            <span>Detail Satuan Ukur: {selectedUomDetail?.code} ({selectedUomDetail?.name})</span>
          </div>
        }
        open={detailModalOpen}
        onCancel={() => setDetailModalOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setDetailModalOpen(false)} style={{ borderRadius: 8, background: '#6366F1' }}>
            Tutup Dialog
          </Button>,
        ]}
        width={640}
      >
        {selectedUomDetail && (
          <div style={{ marginTop: 16 }}>
            <Descriptions bordered column={2} size="small" styles={{ label: { background: '#F8FAFC', width: '35%', fontWeight: 600 } }}>
              <Descriptions.Item label="Kode Satuan">
                <Tag color="cyan" style={{ fontWeight: 800, fontSize: 13 }}>
                  {selectedUomDetail.code}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Nama Lengkap">{selectedUomDetail.name}</Descriptions.Item>
              <Descriptions.Item label="Simbol">
                <Tag style={{ fontWeight: 600 }}>{selectedUomDetail.symbol}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Kategori Dimensi">{selectedUomDetail.category}</Descriptions.Item>
              <Descriptions.Item label="Deskripsi Penggunaan" span={2}>
                {selectedUomDetail.description || 'Tidak ada deskripsi tambahan.'}
              </Descriptions.Item>
            </Descriptions>

            {/* Linked Catalog Items */}
            <div style={{ marginTop: 20 }}>
              <div style={{ fontWeight: 700, color: '#0F172A', marginBottom: 8, fontSize: 13 }}>
                Item Master Katalog yang Menggunakan Satuan Ini ({selectedUomDetail.linkedItems?.length || 0}):
              </div>

              {selectedUomDetail.linkedItems && selectedUomDetail.linkedItems.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                  {selectedUomDetail.linkedItems.map((it) => (
                    <div
                      key={it.id}
                      style={{
                        padding: '8px 12px',
                        background: '#F8FAFC',
                        borderRadius: 8,
                        border: '1px solid #E2E8F0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <strong style={{ color: '#0F172A', fontSize: 12 }}>{it.name}</strong>
                        <span style={{ color: '#6366F1', fontSize: 11, marginLeft: 8 }}>({it.code})</span>
                      </div>
                      <Tag color="purple" style={{ fontSize: 10 }}>{it.category}</Tag>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#94A3B8', fontSize: 12, padding: '12px 0' }}>
                  Belum ada item master katalog yang menggunakan satuan ini.
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
